import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';

import { getActiveActivityKey } from '../utils/activityKeys';
import { ClassroomService } from '../services/llmClient/ClassroomService';
import { updateUnitPagePosition } from '../services/supabaseClient/unitsService';

/**
 * Remove all classroom-related localStorage keys (segment_*, activeActivityIndex, etc)
 */
export const purgeClassroomStorage = () => {
  Object.keys(localStorage).forEach((key) => {
    if (
      key.startsWith('segment_') ||
      key === 'activeActivityIndex' ||
      key.startsWith('activeActivityIndex')
    ) {
      localStorage.removeItem(key);
    }
  });
};

const initialState = {
  activities: [],
  activeActivityIndex: 0,
  activeActivity: null,
  activeCourse: null,
  activeLesson: null,
  previewLesson: null,
  isPreviewLesson: false,
  lessonMetadata: null,
  messages: [],
};

// 🔹 Fetch lesson and check localStorage before hitting LLM
export const generateLessonPlan = createAsyncThunk(
  'classroom/generateLessonPlan',
  async (lessonData, { rejectWithValue }) => {
    try {
      const lesson = await ClassroomService.generateLessonPlan(lessonData);
      if (!lesson || !Array.isArray(lesson.segments) || lesson.segments.length === 0) {
        throw new Error('No lesson activities were generated.');
      }
      return lesson;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to generate lesson plan');
    }
  }
);

// Utility to persist activities and activeActivityIndex
function persistClassroomToLocalStorage(activities, activeActivityIndex) {
  try {
    localStorage.setItem('classroom_activities', JSON.stringify(activities));
    localStorage.setItem('classroom_activeActivityIndex', String(activeActivityIndex));
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[Classroom] Failed to persist classroom state:', e);
  }
}

// Utility to restore activities and activeActivityIndex from localStorage
export function restoreClassroomFromLocalStorage() {
  try {
    const activitiesRaw = localStorage.getItem('classroom_activities');
    const indexRaw = localStorage.getItem('classroom_activeActivityIndex');
    const activities = activitiesRaw ? JSON.parse(activitiesRaw) : [];
    const activeActivityIndex = indexRaw ? parseInt(indexRaw, 10) : 0;
    return { activities, activeActivityIndex };
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[Classroom] Failed to restore classroom state:', e);
    return { activities: [], activeActivityIndex: 0 };
  }
}

// Thunk to sync page position to unit in database
export const syncUnitPagePosition = createAsyncThunk(
  'classroom/syncUnitPagePosition',
  async ({ unitId, pageIndex }, { rejectWithValue }) => {
    try {
      await updateUnitPagePosition(unitId, pageIndex);
      return { unitId, pageIndex };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const classroomSlice = createSlice({
  name: 'classroom',
  initialState,
  reducers: {
    setActiveCourse: (state, action) => {
      const course = action.payload;
      if (!course) {
        state.activeCourse = null;
        state.activeLesson = null;
        return;
      }
      const currentLessonIndex = course.currentLessonIndex ?? 0;
      const activeLesson =
        course.units?.[currentLessonIndex] ?? course.lessons?.[currentLessonIndex] ?? null;

      state.activeCourse = course;
      state.activeLesson = activeLesson;
    },
    setActiveLesson: (state, action) => {
      state.activeLesson = action.payload;
    },
    nextActivity: (state) => {
      const newIndex = Math.min(state.activeActivityIndex + 1, state.activities.length - 1);
      state.activeActivityIndex = newIndex;
      state.activeActivity = state.activities[newIndex] || null;
      const key = getActiveActivityKey(state.activeCourse.id);
      localStorage.setItem(key, String(newIndex)); // Persist
      persistClassroomToLocalStorage(state.activities, newIndex);
    },
    prevActivity: (state) => {
      const newIndex = Math.max(state.activeActivityIndex - 1, 0);
      state.activeActivityIndex = newIndex;
      state.activeActivity = state.activities[newIndex] || null;
      const key = getActiveActivityKey(state.activeCourse.id);
      localStorage.setItem(key, String(newIndex)); // Persist
      persistClassroomToLocalStorage(state.activities, newIndex);
    },
    setActiveActivity: (state, action) => {
      const index = action.payload;
      if (index >= 0 && index < state.activities.length) {
        state.activeActivityIndex = index;
        state.activeActivity = state.activities[index];
        // ✅ Persist to localStorage
        const key = getActiveActivityKey(state.activeCourse.id);
        localStorage.setItem(key, String(index));
        persistClassroomToLocalStorage(state.activities, index);
      }
    },
    addMessageToCurrentActivity: (state, action) => {
      const message = action.payload.message || action.payload;
      const activityId = state.activeActivity.id;
      // Add message to Redux state
      state.activeActivity.messages.push(message);
      // 📌 Persist messages to localStorage
      try {
        const key = `messages-${activityId}`;
        const existing = JSON.parse(localStorage.getItem(key)) || [];
        localStorage.setItem(key, JSON.stringify([...existing, message]));
        // eslint-disable-next-line no-console
        console.log('Persisted message:', message);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to persist message:', err);
      }
    },
    loadMessagesFromLocalStorage: (state) => {
      const index = state.activeActivityIndex;
      const activity = state.activities[index];
      const key = `messages-${activity.id}`;
      try {
        const raw = localStorage.getItem(key);
        const cached = raw ? JSON.parse(raw) : [];
        // eslint-disable-next-line no-console
        console.log(
          '[Redux] Loading messages for activity',
          activity.id,
          'from localStorage:',
          cached
        );
        if (Array.isArray(cached)) {
          state.activities[index].messages = cached;
          // Optionally sync activeActivity reference
          state.activeActivity.messages = cached;
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn(`[Redux] Failed to load messages for activity ${activity.id}:`, err);
      }
    },
    setActivityContent: (state, action) => {
      const index = state.activeActivityIndex;
      // Accept either { content } or direct string
      const content = action.payload.content ?? action.payload;
      if (index < state.activities.length) {
        state.activities[index].content = content;
        localStorage.setItem(`segment_${state.activities[index].id}`, content);
        state.activeActivity = state.activities[index];
      }
    },
    setPreviewLesson: (state, action) => {
      state.previewLesson = action.payload;
      state.isPreviewLesson = Boolean(action.payload);
    },
    clearLessonPlan: (state) => {
      state.activities = [];
      state.activeActivityIndex = 0;
      state.activeActivity = null;
      state.previewLesson = null;
      state.lessonMetadata = null;
      persistClassroomToLocalStorage([], 0);
    },
    /**
     * Reset classroom state and purge all classroom-related localStorage keys
     * Usage: dispatch(resetClassroomState())
     */
    resetClassroomState: () => {
      purgeClassroomStorage();
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(generateLessonPlan.fulfilled, (state, action) => {
      const payload = action.payload || {};
      const segments = payload.segments || [];
      const lessonMetadata = payload.lessonMetadata || {};

      // ✅ Restore page index from multiple sources (priority order)
      const courseId = state.activeCourse?.id;
      const key = getActiveActivityKey(courseId);

      // 1. Try localStorage (most recent session - highest priority)
      const storedIndex = parseInt(localStorage.getItem(key), 10);

      // 2. Try unit's saved page index (from database)
      const unitPageIndex = state.activeLesson?.currentPageIndex ?? 0;

      // 3. Use the most recent valid index
      let targetIndex = 0;
      if (!isNaN(storedIndex) && storedIndex >= 0 && storedIndex < segments.length) {
        targetIndex = storedIndex;
      } else if (unitPageIndex >= 0 && unitPageIndex < segments.length) {
        targetIndex = unitPageIndex;
      }

      state.activities = segments.map((activity) => ({
        ...activity,
        messages: [],
        content: localStorage.getItem(`segment_${activity.id}`) || '',
      }));
      state.lessonMetadata = lessonMetadata;
      state.activeActivityIndex = targetIndex;
      state.activeActivity = state.activities[targetIndex] || null;
      state.messages = [];
    });
    builder.addCase(generateLessonPlan.rejected, (state, action) => {
      const lessonTitle = action?.meta?.arg?.lessonTitle || 'Lesson';
      const lessonObjective = action?.meta?.arg?.lessonObjective || 'Continue learning';
      state.activities = [
        {
          id: uuidv4(),
          sequence: 1,
          leader: 'Professor',
          title: lessonTitle,
          objective: lessonObjective,
          assessment: '',
          contentSeed: {
            summary:
              action.payload ||
              action.error?.message ||
              'Lesson generator unavailable. Showing fallback content.',
            keyQuestions: [],
            deliverables: [],
            learningGoals: [],
            references: [],
          },
          messages: [],
        },
      ];
      state.activeActivityIndex = 0;
      state.activeActivity = state.activities[0];
      state.messages = [];
    });
  },
});

export const {
  setActiveCourse,
  setActiveLesson,
  nextActivity,
  prevActivity,
  addMessageToCurrentActivity,
  setActivityContent,
  clearLessonPlan,
  resetClassroomState,
  setActiveActivity,
  setPreviewLesson,
  loadMessagesFromLocalStorage,
} = classroomSlice.actions;

export default classroomSlice.reducer;

export const selectClassroom = (state) => state.classroom;
export const selectActiveActivity = (state) => state.classroom.activeActivity;
export const selectActiveActivityContent = (state) => state.classroom.activeActivity?.content;
export const selectActiveCourse = (state) => state.classroom.activeCourse;
export const selectActiveActivityIndex = (state) => state.classroom.activeActivityIndex;
export const selectActivities = (state) => state.classroom.activities;
export const selectActiveLesson = (state) => state.classroom.activeLesson;
export const selectLessonMetadata = (state) => state.classroom.lessonMetadata;
export const selectMessages = (state) => state.classroom.messages;
